import { WifiOff, CloudOff, RefreshCw, Clock, AlertTriangle, Database, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';

/**
 * Offline Mode Banner Component
 * Displays when the app is offline and shows pending sync status
 */
export default function OfflineBanner({ 
  isOffline,
  isOnline, 
  isServerReachable, 
  pendingCount, 
  isSyncing,
  syncProgress,
  lastSyncTime,
  hasConflicts,
  onSync,
  onShowConflicts
}) {
  // Calculate sync progress percentage
  const syncPercent = syncProgress?.total > 0 
    ? Math.round((syncProgress.completed / syncProgress.total) * 100) 
    : 0;

  const formatLastSync = () => {
    if (!lastSyncTime) return 'Never';
    const diff = Date.now() - new Date(lastSyncTime).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'Just now';
    if (minutes === 1) return '1 min ago';
    if (minutes < 60) return `${minutes} mins ago`;
    const hours = Math.floor(minutes / 60);
    if (hours === 1) return '1 hour ago';
    if (hours < 24) return `${hours} hours ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  };

  // Syncing in progress
  if (isSyncing) {
    return (
      <div 
        className="fixed top-0 left-0 right-0 z-50 bg-blue-600 text-white px-4 py-3"
        data-testid="syncing-banner"
      >
        <div className="container mx-auto">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <RefreshCw className="w-5 h-5 animate-spin" />
              <span className="font-bold">Syncing...</span>
              <span className="text-blue-100 text-sm">
                {syncProgress?.completed || 0} of {syncProgress?.total || 0} items
              </span>
            </div>
            <span className="text-sm">{syncPercent}%</span>
          </div>
          <Progress value={syncPercent} className="h-1 bg-blue-400" />
        </div>
      </div>
    );
  }

  // Full offline (no network)
  if (!isOnline) {
    return (
      <div 
        className="fixed top-0 left-0 right-0 z-50 bg-red-600 text-white px-4 py-3"
        data-testid="offline-banner"
      >
        <div className="container mx-auto flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 animate-pulse">
              <WifiOff className="w-5 h-5" />
              <span className="font-bold text-lg">OFFLINE MODE</span>
            </div>
            <span className="text-red-100 text-sm hidden sm:inline">
              No internet - all changes saved locally
            </span>
          </div>
          
          <div className="flex items-center gap-3">
            {pendingCount > 0 && (
              <Badge className="bg-red-500 text-white border-red-400">
                <Database className="w-3 h-3 mr-1" />
                {pendingCount} pending
              </Badge>
            )}
            {hasConflicts && (
              <Button
                size="sm"
                variant="outline"
                onClick={onShowConflicts}
                className="text-white border-white hover:bg-red-500"
              >
                <AlertTriangle className="w-4 h-4 mr-1" />
                Conflicts
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Server unreachable (network on but server down)
  if (!isServerReachable) {
    return (
      <div 
        className="fixed top-0 left-0 right-0 z-50 bg-orange-500 text-white px-4 py-3"
        data-testid="server-unreachable-banner"
      >
        <div className="container mx-auto flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <CloudOff className="w-5 h-5" />
              <span className="font-bold">Server Unreachable</span>
            </div>
            <span className="text-orange-100 text-sm hidden sm:inline">
              Working offline - changes will sync when connection restored
            </span>
          </div>
          
          <div className="flex items-center gap-3">
            {pendingCount > 0 && (
              <Badge className="bg-orange-400 text-white">
                <Database className="w-3 h-3 mr-1" />
                {pendingCount} pending
              </Badge>
            )}
            {hasConflicts && (
              <Button
                size="sm"
                variant="outline"
                onClick={onShowConflicts}
                className="text-white border-white hover:bg-orange-400"
              >
                <AlertTriangle className="w-4 h-4 mr-1" />
                Conflicts
              </Button>
            )}
            <Button 
              size="sm" 
              variant="outline" 
              onClick={onSync}
              className="text-white border-white hover:bg-orange-400"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Online but has pending orders to sync
  if (pendingCount > 0 || hasConflicts) {
    return (
      <div 
        className="fixed top-0 left-0 right-0 z-50 bg-blue-500 text-white px-4 py-3"
        data-testid="sync-pending-banner"
      >
        <div className="container mx-auto flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            {hasConflicts ? (
              <>
                <AlertTriangle className="w-5 h-5 text-yellow-300" />
                <span className="font-medium">
                  {pendingCount} pending, {syncProgress?.conflicts || 0} conflict{(syncProgress?.conflicts || 0) !== 1 ? 's' : ''} to resolve
                </span>
              </>
            ) : (
              <>
                <Database className="w-5 h-5" />
                <span className="font-medium">
                  {pendingCount} item{pendingCount > 1 ? 's' : ''} waiting to sync
                </span>
              </>
            )}
            {lastSyncTime && (
              <span className="text-blue-100 text-sm hidden sm:flex items-center gap-1">
                <Clock className="w-4 h-4" />
                Last sync: {formatLastSync()}
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {hasConflicts && (
              <Button
                size="sm"
                variant="outline"
                onClick={onShowConflicts}
                className="text-white border-white hover:bg-blue-400"
              >
                <AlertTriangle className="w-4 h-4 mr-1" />
                Resolve
              </Button>
            )}
            <Button 
              size="sm" 
              variant="outline" 
              onClick={onSync}
              className="text-white border-white hover:bg-blue-400"
              data-testid="sync-now-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Sync Now
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

/**
 * Compact offline indicator for headers/toolbars
 */
export function OfflineIndicator({ 
  isOffline, 
  pendingCount, 
  isSyncing,
  hasConflicts,
  onSync,
  onShowConflicts
}) {
  if (!isOffline && pendingCount === 0 && !hasConflicts) {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      {isOffline ? (
        <Badge variant="destructive" className="animate-pulse">
          <WifiOff className="w-3 h-3 mr-1" />
          Offline
        </Badge>
      ) : hasConflicts ? (
        <Badge 
          variant="secondary" 
          className="cursor-pointer bg-amber-100 text-amber-700 hover:bg-amber-200"
          onClick={onShowConflicts}
        >
          <AlertTriangle className="w-3 h-3 mr-1" />
          Conflicts
        </Badge>
      ) : pendingCount > 0 ? (
        <Badge 
          variant="secondary" 
          className="cursor-pointer hover:bg-slate-200"
          onClick={onSync}
          data-testid="pending-sync-badge"
        >
          <RefreshCw className={`w-3 h-3 mr-1 ${isSyncing ? 'animate-spin' : ''}`} />
          {pendingCount} pending
        </Badge>
      ) : null}
    </div>
  );
}

/**
 * Sync status indicator (shows checkmark when fully synced)
 */
export function SyncStatusIndicator({ isOnline, isServerReachable, pendingCount, lastSyncTime }) {
  if (!isOnline || !isServerReachable) return null;
  
  if (pendingCount === 0) {
    return (
      <div className="flex items-center gap-1 text-green-600 text-sm">
        <CheckCircle className="w-4 h-4" />
        <span className="hidden sm:inline">Synced</span>
      </div>
    );
  }

  return null;
}

/**
 * Full-screen offline overlay for critical operations
 */
export function OfflineOverlay({ isOffline, children }) {
  if (!isOffline) return children;

  return (
    <div className="relative">
      {children}
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-40">
        <div className="bg-white rounded-2xl p-8 shadow-2xl text-center max-w-md mx-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <WifiOff className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-2">You're Offline</h3>
          <p className="text-slate-500 mb-4">
            This operation requires an internet connection. Please check your network and try again.
          </p>
          <Button 
            variant="outline" 
            onClick={() => window.location.reload()}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh Page
          </Button>
        </div>
      </div>
    </div>
  );
}
